import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { PurchasedFile, PurchasedFileDetails } from "@scrile/api-provider/dist/api/PurchasedFileProvider";
import { VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import { WelcomeMessageFile } from "@scrile/api-provider/dist/api/WelcomeMessagesProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile, PostFileData } from "@/types";

export { getFileMimeType, getNameMimeType } from "@scrile/tools/dist/lib/FileHelpers";

export type FileType =
  | ContentFile
  | PostFileData
  | FileData
  | AttachmentFile
  | VaultFile
  | WelcomeMessageFile
  | PurchasedFileDetails
  | PurchasedFile
  | File;

function getMimeType(f: FileType): string {
  if ("lastModify" in f) {
    return getFileMimeType(f);
  }
  if ("mediaType" in f) {
    return f.mediaType;
  } else if ("type" in f) {
    return f.type;
  } else if ("contentType" in f && f.contentType) {
    return f.contentType;
  } else {
    return f.metadata.contentType || "";
  }
}

export function isVideoFile(f: FileType) {
  return getMimeType(f).startsWith("video");
}

export function isImageFile(f: FileType) {
  return getMimeType(f).startsWith("image");
}

export function isAudioFile(f: FileType) {
  return getMimeType(f).startsWith("audio");
}

export function isMediaFile(f: FileType) {
  return isVideoFile(f) || isImageFile(f);
}

export function isPostFileData(f: ContentFile | PostFileData): f is PostFileData {
  return Reflect.has(f, "uploadProgress");
}

export function getFilesIcon(files: ContentFile[]) {
  const result = files.reduce<{ image: number; video: number; audio: number; files: number }>(
    (out, f) => {
      if (isVideoFile(f)) out.video++;
      else if (isImageFile(f)) out.image++;
      else if (isAudioFile(f)) out.audio++;
      else out.files++;
      return out;
    },
    { image: 0, video: 0, audio: 0, files: 0 }
  );
  if (result.image === 1 && !result.audio && !result.video && !result.files) return "";
  else if (result.files === 1 && !result.audio && !result.video && !result.image) return "";
  else if (result.video === 1 && !result.image && !result.audio && !result.files) return "sci-play";
  else if (result.audio === 1 && !result.image && !result.video && !result.files) return "sci-music-box";
  return "sci-file-multiple";
}

export function getPostAllowedFileTypes(allowedTypeKeys?: string[]) {
  return config.postAllowedFileTypes.reduce((out, item) => {
    type FileTypeKeys = keyof typeof config.fileTypes;
    const isConfigAcceptType = (type: string): type is FileTypeKeys => {
      if (!!allowedTypeKeys) {
        return Object.keys(config.fileTypes).includes(type) && allowedTypeKeys.includes(type);
      }
      return Object.keys(config.fileTypes).includes(type);
    };
    if (isConfigAcceptType(item)) {
      out.push(item);
    } else {
      for (let type in config.fileTypes) {
        if (isConfigAcceptType(type) && config.fileTypes[type].includes(item.replace(".", ""))) {
          out.push(item.startsWith(".") ? item : `.${item}`);
        }
      }
    }
    return out;
  }, [] as string[]);
}

export const isAttachmentFile = (file: AttachmentFile | InputAttachmentFile | File): file is AttachmentFile => {
  return "uid" in file;
};
export const isInputAttachmentFile = (file: AttachmentFile | InputAttachmentFile): file is InputAttachmentFile => {
  return "file" in file;
};
export const isFile = (file: File | AttachmentFile): file is File => {
  return "lastModified" in file;
};

export const attachmentFileAdapter = (files: VaultFile[], publicAccess = false): AttachmentFile[] => {
  return files.map(file => ({
    src: isVideoFile(file)
      ? MediaFilesController.getVideoThumbnail(file.urlPart, "240p")
      : MediaFilesController.getImageThumbnail(file.urlPart, "240p"),
    uid: file.uuid,
    type: file.contentType,
    publicAccess,
  }));
};
